import classNames from 'classnames';
import { JSX, type ReactNode } from 'react';
import atTagVariants from './at-tag.variants';
import { EClickableSize } from '@/components/atoms/at-clickable/at-clickable.types';
import { EIcons } from '../at-icon/icons';
import { AtIcon } from '../at-icon';
import { sendGTMClickEvent } from '@/components/helpers/analytics/gtm';
import { ETagVariants } from './at-tag.types';

export interface IAtTagProps {
  variant: ETagVariants;
  size?: EClickableSize;
  children: ReactNode;
  onClick?: () => void;
  onMouseEnter?: () => void;
  onMouseLeave?: () => void;
  icon?: EIcons;
  className?: string;
}

const RenderIcon = (variant: ETagVariants, icon: EIcons | undefined) => {
  if (icon) {
    return <AtIcon icon={icon} />;
  }

  if (variant === ETagVariants.OFFERS) {
    return (
      <span className="text-2xl">
        <AtIcon icon={EIcons.PRICE} color="var(--color-white)" />
      </span>
    );
  }

  if (variant === ETagVariants.LOCATION) {
    return (
      <span className="text-2xl">
        <AtIcon
          icon={EIcons.LOCATION}
          className="[&_path]:fill-chiclet-location-default group-hover:[&_path]:fill-chiclet-location-hover"
        />
      </span>
    );
  }
};

/**
 * `AtTag` is a flexible tag component used to display styled labels or filters.
 * It conditionally renders as a button (when interactive) or a span (when static),
 * and supports optional icons, variant-based styling, and GTM click tracking.
 *
 */

export const AtTag = ({
  variant,
  children,
  size = EClickableSize.SM,
  onClick,
  icon,
  className,
  onMouseEnter,
  onMouseLeave,
}: IAtTagProps): JSX.Element => {
  const tagClasses = atTagVariants({ variant, size });

  const handleClick = () => {
    sendGTMClickEvent(icon || variant, 'at-tag', 'not_available');

    !!onClick && onClick();
  };

  if (onClick) {
    return (
      <button
        onClick={handleClick}
        onMouseEnter={onMouseEnter}
        onMouseLeave={onMouseLeave}
        className={classNames(tagClasses, 'group', className)}
      >
        {RenderIcon(variant, icon)}
        {children}
      </button>
    );
  }
  return (
    <span
      className={classNames(tagClasses, 'group', className)}
      onMouseEnter={onMouseEnter}
      onMouseLeave={onMouseLeave}
    >
      {RenderIcon(variant, icon)}
      {children}
    </span>
  );
};
